import { h, defineComponent, ref, watch } from 'vue';
import { t } from '@/ui/i18n/i18n';
import { box, container, loader, loadingButton, pageTitle } from '@/ui/components';
import { useTask } from '@/ui/concerns';
import { get, intoEditable, update } from '@/services/api/resources/entityFolder';
import { remove as removeLabel, filter as fetchAllLabels, filterEntityFolderLabelsToOptions } from '@/services/api/resources/entityFolderLabel';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as fetchAllEntityItems } from '@/services/api/resources/entityItem';

import { filterListToOptions, IdProps, sequence, toastAndRedirect } from '@/ui/helpers/uiHelper';
import { versioned } from '@/services/api';
import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
import { SelectOptions, SelectOptionsWithId } from '@/ui/types';
import { useEntityFolderForm } from './useEntityFolderForm';
import { useEntityFolderEditRecord } from './useEntityFolderRecord';
import { renderForbiddenPage } from '../error/ForbiddenPage';
import { renderResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';
import { queryTabName, EntityItemsTabNames } from '@/ui/components/navigation/constants';
import { fetchSelectOptions } from './EntityFolderChildrenTransforms';
import { RemoveLabelConfirmationModal } from '@/ui/components/entityFolderLabel/RemoveLabelConfirmationModal';
import { pipe } from 'fp-ts/lib/pipeable';

export const EntityFolderEditPage = defineComponent<IdProps>({
  name: 'EntityFolderEditPage',
  props: ['id'],
  setup(props) {
    if (!hasFeature('entityFolders')) {
      return renderResourceTeaserPage(t('common.entity-folders'));
    }

    if (!hasPermission(Permissions.MANAGE_ENTITY_FOLDERS)) {
      return renderForbiddenPage;
    }

    let recordVersion = '';
    const isRemovingLabel = ref<boolean>(false);
    const isRemoveLabelModalOpen = ref<boolean>(false);
    const labelToBeRemoved = ref<number>(0);
    const entityItemOptions = ref<SelectOptions>([]);
    const childrenOptions = ref<SelectOptions>([]);
    const userOptions = ref<SelectOptions>([]);
    const labelOptions = ref<SelectOptionsWithId<string>>([]);

    // create empty record
    const record = useEntityFolderEditRecord();

    // get record
    const getRecord = useTask(
      () => sequence(get(props.id), getAllUsers(), fetchAllEntityItems(), fetchAllLabels()),
      ([{ data, version }, users, entityItems, labels]) => {
        userOptions.value = filterListToOptions(users);
        entityItemOptions.value = filterListToOptions(entityItems);
        labelOptions.value = filterEntityFolderLabelsToOptions(labels);
        record.value = intoEditable(data);
        recordVersion = version;

        fetchSelectOptions(props.id, childrenOptions, record).run();
      },
    );

    const openRemoveModalTask = (label: string) => pipe(() => {
      isRemoveLabelModalOpen.value = true;
      labelToBeRemoved.value = labelOptions.value.find(row => row.label === label)?.id || 0;
    });
    const removeLabelTask = useTask(
      () => removeLabel(labelToBeRemoved.value),
      () => {
        isRemoveLabelModalOpen.value = false;
        labelToBeRemoved.value = 0;

        getRecord.run();
      },
    );
    watch(ref(props.id), getRecord.run);

    // send record
    const sendRecord = useTask(
      () => update(props.id, versioned(record.value, recordVersion)),
      () => toastAndRedirect(t('entity-folder.edited'), 'entityItems', { [queryTabName]: EntityItemsTabNames.FOLDERS }),
    );

    const { formFields, handleSubmit } = useEntityFolderForm(record, sendRecord, openRemoveModalTask);
    return function render() {
      return container(
        [
          h(RemoveLabelConfirmationModal, {
            props: { active: isRemoveLabelModalOpen.value, loading: isRemovingLabel.value },
            on: { input: () => { isRemoveLabelModalOpen.value = false; }, 'click:removeLabel': () => removeLabelTask.run() },
          }),
          pageTitle(t('entity-folder.edit')),
          box([loader('form', getRecord, [
            ...formFields(
              entityItemOptions.value, childrenOptions.value, userOptions.value, labelOptions.value,
              fetchSelectOptions.bind(fetchSelectOptions, props.id, childrenOptions),
            ),
            loadingButton(
              t('entity-folder.update'),
              handleSubmit,
              sendRecord.state.value.pending,
            ),
          ])]),
        ],
        'narrow',
      );
    };
  },
});
